// AssetTVPage.js — versão TV/BROADCAST da AssetPage (RRTV)
// ============================================================
// Chart de ativo LIMPO pra vídeo/OBS: sem Header, sem Silk, sem nav.
// Fundo TRANSPARENTE (o Wan talking-head / OBS fica atrás), TV-sized (1920×1080).
// DETERMINÍSTICO por padrão: trava num timeframe (3M se nada vier) — quem manda a
// troca é o cue/TSV via screenshot, NÃO um timer interno.
// ?tf=5D|1M|3M|6M|YTD|12M|MAX hard-set;  ?days=N janela ARBITRÁRIA (casa a fala);
// ?view=number → variação INTRADAY grande, SEM gráfico (mostra na 1ª menção do ativo);
// ?rotate[=seg] religa a auto-rotação só pra preview/OBS ao vivo.
// Padrão por ativo (cue): view=number (intraday) → gráfico 1M/3M/12M sincronizado à fala.
//
// Rota sugerida (app.js):
//   <Route path="/tv/asset/:type/:symbol" element={<AssetTVPage/>} />
// Ex.: /tv/asset/stock/PETR4           → fixo em 3M (default, pro cue)
//      /tv/asset/stock/PETR4?tf=YTD     → hard-set no ano
//      /tv/asset/stock/QCOM?rotate=6    → auto-rotate a cada 6s (só preview/OBS)
//
// Reusa a MESMA fonte da AssetPage: /api/go/api/yahoo/v2/{ticker}.

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer,
} from 'recharts';

const API_URL = process.env.REACT_APP_API_URL || '/api/go';
const DINO_API = '/api/tickers61';   // add near API_URL
const LOGO_HOST = '/api/logos';
const ROXO = '#6660fe';   // RR Magma — roxo é a cor da marca
const INK = '#0e0d1f';

const TF = [
  { key: '5D',  label: '5 DIAS',    days: 5,     span: 'nos últimos 5 dias' },
  { key: '1M',  label: '1 MÊS',     days: 30,    span: 'no mês' },
  { key: '3M',  label: '3 MESES',   days: 90,    span: 'em 3 meses' },
  { key: '6M',  label: '6 MESES',   days: 180,   span: 'em 6 meses' },
  { key: 'YTD', label: 'NO ANO',    days: 'ytd', span: 'no ano' },
  { key: '12M', label: '12 MESES',  days: 365,   span: 'em 12 meses' },
  { key: 'MAX', label: 'HISTÓRICO', days: 'max', span: 'no histórico' },
];

// ?days=N → timeframe ARBITRÁRIO (o cue pede a janela exata da fala)
function tfForDays(n) {
  const lbl = n % 365 === 0 ? `${n / 365} ANO${n / 365 > 1 ? 'S' : ''}`
            : n % 30 === 0 ? `${n / 30} ${n / 30 > 1 ? 'MESES' : 'MÊS'}`
            : `${n} DIAS`;
  return { key: `D${n}`, label: lbl, days: n, span: `nos últimos ${n} dias` };
}

async function fetchSeries(ticker) {
  try {
    const r = await fetch(`${API_URL}/api/yahoo/v2/${ticker}`);
    if (!r.ok) return null;
    const d = await r.json();
    if (!d.candles?.length) return null;
    return d.candles
      .map(c => ({ ts: c.ts, date: c.date, close: c.close, high: c.high, low: c.low }))
      .filter(p => p.close != null);
  } catch { return null; }
}

function sliceByTF(all, tf) {
  if (!tf || tf.days === 'max') return all;
  if (tf.days === 'ytd') {
    const y0 = new Date(new Date().getFullYear(), 0, 1).getTime() / 1000;
    return all.filter(p => p.ts >= y0);
  }
  const cut = Math.floor(Date.now() / 1000) - tf.days * 86400;
  return all.filter(p => p.ts >= cut);
}

export default function AssetTVPage() {
  const { symbol } = useParams();
  const [params] = useSearchParams();
  const fixedTf = (params.get('tf') || '').toUpperCase();
  const hasRotate = params.has('rotate');            // rotaciona SÓ se pedir ?rotate
  const rotateSec = Number(params.get('rotate')) || 6;
  const customDays = Number(params.get('days')) || 0; // janela ARBITRÁRIA pro cue
  const view = (params.get('view') || 'chart').toLowerCase(); // 'number' = intraday, sem gráfico
  // presets + o arbitrário; determinístico por padrão (trava em ?days/?tf, ou 3M)
  const TFX = customDays ? [...TF, tfForDays(customDays)] : TF;
  const lockedTf = customDays ? `D${customDays}` : (fixedTf || '3M');

  const [all, setAll] = useState([]);
  const [meta, setMeta] = useState(null);
  const [idx, setIdx] = useState(() => {
    const startKey = (hasRotate && !fixedTf && !customDays) ? '5D' : lockedTf;
    return Math.max(0, TFX.findIndex(t => t.key === startKey));
  });
  const [logoOk, setLogoOk] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      const s = await fetchSeries(symbol);
      if (!alive) return;
      setAll(s || []);
      // meta: preço e nome via /api/ticker (mesmo da AssetPage)
      try {
        const r = await fetch(`${API_URL}/api/ticker/${symbol}`);
        const rr = r.ok ? r : await fetch(`${DINO_API}/api/ticker/${symbol}`);
        if (rr.ok) setMeta(await rr.json());
      } catch { /* noop */ }
    })();
    return () => { alive = false; };
  }, [symbol]);

  // auto-rotate SÓ com ?rotate explícito (e sem ?tf/?days travado). Default = fixo.
  useEffect(() => {
    if (!hasRotate || fixedTf || customDays) return;
    const t = setInterval(() => setIdx(i => (i + 1) % TFX.length), rotateSec * 1000);
    return () => clearInterval(t);
  }, [hasRotate, fixedTf, customDays, rotateSec, TFX.length]);

  const tf = TFX[idx] || TFX[0];
  const data = useMemo(() => sliceByTF(all, tf), [all, tf]);
  const ret = useMemo(() => {
    if (data.length < 2) return null;
    return (data[data.length - 1].close - data[0].close) / data[0].close * 100;
  }, [data]);
  const up = (ret ?? 0) >= 0;
  const color = ret == null ? ROXO : (up ? '#22c55e' : '#ef4444');

  const ccy = meta?.currency || 'BRL';
  const noDec = ['JPY', 'KRW', 'IDR', 'CLP', 'TWD'].includes(ccy);
  const price = meta?.ult != null
    ? (ccy === 'BRL' ? `R$ ${meta.ult.toFixed(2)}` : `${ccy} ${noDec ? Math.round(meta.ult) : meta.ult.toFixed(2)}`)
    : '';
  const base = (symbol || '').split('.')[0].toUpperCase();

  return (
    // 1920×1080 transparente. data-tf/data-ret pra automação do screenshot.
    <div data-tf={tf.key} data-ret={ret == null ? '' : ret.toFixed(2)}
      style={{ width: '1920px', height: '1080px', background: 'transparent',
               position: 'relative', overflow: 'hidden', color: '#fff',
               fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>

      {/* Lower-third header (oculto no view=number, que já tem card próprio) */}
      {view !== 'number' && (
      <div style={{ position: 'absolute', top: 90, left: 120, right: 120,
                    display: 'flex', alignItems: 'center', gap: 28 }}>
        {logoOk && base && (
          <img src={`${LOGO_HOST}/${base}.svg`} alt="" onError={() => setLogoOk(false)}
               style={{ height: 96, width: 96, objectFit: 'contain',
                        filter: 'drop-shadow(0 4px 10px rgba(0,0,0,.6))' }} />
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "'Roble', Georgia, serif", fontSize: 64, fontWeight: 800,
                        color: ROXO, lineHeight: 1 }}>{meta?.name || base}</div>
          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 30,
                        color: 'rgba(255,255,255,.7)', marginTop: 8 }}>{base}</div>
        </div>
        {price && (
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 68, fontWeight: 700 }}>{price}</div>
          </div>
        )}
      </div>
      )}

      {/* view=number → card INTRADAY compacto à ESQUERDA (estilo Bloomberg), sem gráfico.
          Deixa o centro/direita pro âncora. Mostra na 1ª menção do ativo. */}
      {view === 'number' && (() => {
        const v = meta?.var;
        const dup = (v ?? 0) >= 0;
        const dc = v == null ? ROXO : (dup ? '#22c55e' : '#ef4444');
        const p = meta?.ult;
        const abs = (p != null && v != null) ? Math.abs(p - p / (1 + v / 100)) : null;
        const pstr = p == null ? '—' : (noDec ? Math.round(p).toString() : p.toFixed(2));
        return (
          <div style={{ position: 'absolute', left: '50%', top: '50%',
                        transform: 'translate(-50%, -50%)', width: 1040,
                        background: 'rgba(14,13,31,0.84)', border: '1px solid rgba(255,255,255,0.12)',
                        borderRadius: 30, padding: '56px 68px', backdropFilter: 'blur(10px)',
                        boxShadow: '0 20px 70px rgba(0,0,0,0.6)', animation: 'rrtvFade .45s ease-out' }}>
            <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 40,
                          letterSpacing: '.26em', color: 'rgba(255,255,255,.65)', marginBottom: 24 }}>
              INTRADAY
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 26, marginBottom: 30 }}>
              {logoOk && base && (
                <img src={`${LOGO_HOST}/${base}.svg`} alt="" onError={() => setLogoOk(false)}
                     style={{ height: 104, width: 104, objectFit: 'contain', borderRadius: 12,
                              filter: 'drop-shadow(0 4px 12px rgba(0,0,0,.6))' }} />
              )}
              <div style={{ display: 'inline-block', background: ROXO, color: '#fff',
                            fontFamily: "'Roble', Georgia, serif", fontWeight: 800, fontSize: 66,
                            padding: '8px 28px', borderRadius: 14, lineHeight: 1.1 }}>
                {meta?.name || base}
              </div>
            </div>
            <div style={{ fontSize: 170, fontWeight: 800, color: '#fff', lineHeight: 0.98, marginBottom: 24 }}>
              {(ccy === 'BRL' ? 'R$ ' : ccy + ' ') + pstr}
            </div>
            {v != null && (
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 48, flexWrap: 'nowrap' }}>
                <div style={{ fontSize: 96, fontWeight: 800, color: dc, lineHeight: 1.0 }}>
                  {dup ? '▲' : '▼'} {abs != null ? abs.toFixed(2) : ''}
                </div>
                <div style={{ fontSize: 96, fontWeight: 800, color: dc, lineHeight: 1.0 }}>
                  {(dup ? '+' : '') + v.toFixed(2)}%
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {view !== 'number' && (<>
      {/* Timeframe pill + retorno (o que roda com a narração) */}
      <div key={tf.key} style={{ position: 'absolute', top: 250, left: 120,
                                 display: 'flex', alignItems: 'baseline', gap: 28,
                                 animation: 'rrtvFade .45s ease-out' }}>
        <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 44, fontWeight: 700,
                       color: ROXO, letterSpacing: '.06em',
                       border: `2px solid ${ROXO}`, borderRadius: 10, padding: '6px 22px' }}>
          {tf.label}
        </span>
        {ret != null && (
          <span style={{ fontSize: 72, fontWeight: 800, color }}>
            {up ? '▲' : '▼'} {up ? '+' : ''}{ret.toFixed(2)}%
          </span>
        )}
      </div>

      {/* O gráfico — hero, ocupa a metade de baixo */}
      <div key={tf.key + '-chart'} style={{ position: 'absolute', left: 80, right: 80,
                                            top: 380, height: 620,
                                            animation: 'rrtvFade .45s ease-out' }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 20, left: 20, bottom: 10 }}>
            <defs>
              <linearGradient id="tvGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.45} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="4 4" stroke="rgba(255,255,255,0.08)" />
            <XAxis dataKey="date" stroke="rgba(255,255,255,0.5)" fontSize={18}
                   interval="preserveStartEnd" minTickGap={120} tickMargin={12} />
            <YAxis stroke="rgba(255,255,255,0.5)" fontSize={18} width={90}
                   domain={['dataMin', 'dataMax']}
                   tickFormatter={v => (ccy === 'BRL' ? 'R$ ' : '') + Number(v).toFixed(noDec ? 0 : 2)} />
            <Area type="monotone" dataKey="close" stroke={color} strokeWidth={4}
                  fill="url(#tvGrad)" isAnimationActive />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      </>)}

      <style>{`@keyframes rrtvFade { from { opacity: 0; transform: translateY(10px);} to { opacity: 1; transform: translateY(0);} }`}</style>
    </div>
  );
}
